import { DEFAULT_CARD_TIMER_UNITS, DEFAULT_TIMER_VALUE } from "@/lib/const";
import { Input } from "@/components/Base/Input";
import {
  capitalize,
  convertHoursToMinutes,
  convertMinutesToHours,
} from "@/lib/utils";
import { Button } from "@/components/Base/Button";
import { CheckboxInput } from "@/components/Base/CheckboxInput";
import { Shop } from "@/types/v2";
import { shop as shopRepository } from "@/repositories/shop";
import { showSuccessToast } from "@/lib/toast";
import { useEffect, useMemo, useState } from "react";

type TimerSettingsProps = {
  shopData: Shop;
};

export const TimerSettings = ({ shopData }: TimerSettingsProps) => {
  const shopId = useMemo(() => shopData.id, [shopData]);

  const [defaultTimerValue, setDefaultTimerValue] =
    useState<string>(DEFAULT_TIMER_VALUE);

  const timerUnits = useMemo(
    () => shopData?.cardConfig?.timerUnits ?? DEFAULT_CARD_TIMER_UNITS,
    [shopData]
  );

  useEffect(() => {
    if (!shopData) return;
    setDefaultTimerValue(shopData.defaultTimer ?? DEFAULT_TIMER_VALUE);
  }, [shopData]);

  const updateDefaultTimerValue = () => {
    if (!shopId || !defaultTimerValue) return;
    shopRepository
      .updateDefaultTimerValue(shopId, defaultTimerValue)
      .then(() => {
        showSuccessToast(
          "Default Timer Updated",
          "Return to the dashboard to see the updated default timer."
        );
      });
  };

  const handleTimerUnitsClicked = (
    timerUnits: Shop["cardConfig"]["timerUnits"]
  ) => {
    if (!shopId) return;
    shopRepository.updateTimerUnits(shopId, timerUnits).then(() => {
      showSuccessToast(
        `Timer Units Set to ${timerUnits ? capitalize(timerUnits) : "Unknown"}`,
        "Return to the dashboard to see the updated default timer units."
      );
    });
  };

  const handleUpdateDefaultTimerValue = (value: string) => {
    if (timerUnits === "minutes") {
      return setDefaultTimerValue(value);
    }
    return setDefaultTimerValue(convertHoursToMinutes(value));
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-xl">Default Timer Value:</h3>
        <div className="pb-2.5">
          in {timerUnits ?? DEFAULT_CARD_TIMER_UNITS}
        </div>
        <div className="flex flex-row gap-4">
          <Input
            type="number"
            placeholder="15"
            value={
              timerUnits === "minutes"
                ? parseFloat(defaultTimerValue)
                : parseFloat(convertMinutesToHours(defaultTimerValue))
            }
            onChange={(e) => handleUpdateDefaultTimerValue(e.target.value)}
            className="text-primary"
          />
          <Button
            type="submit"
            onClick={updateDefaultTimerValue}
            variant={"accent"}
          >
            Update
          </Button>
        </div>
      </div>
      <div>
        <h3 className="pb-2.5 text-xl">Timer Units:</h3>
        <div className="flex flex-col gap-4">
          <CheckboxInput
            aria-label={"Timer in Minutes"}
            label={"Timer in Minutes"}
            checked={
              timerUnits === undefined ||
              timerUnits === null ||
              timerUnits === "minutes"
            }
            onCheckedChange={(checked) =>
              handleTimerUnitsClicked(checked ? "minutes" : "hours")
            }
          />
        </div>
      </div>
    </div>
  );
};
